import './stock-bounds.scss';

import { Switch } from 'antd';
import Tooltip from 'antd/lib/tooltip';
import { useContext, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import InfoIcon from '@/media/icons/info-circle.svg?react';

import { CreateTabContext } from '../../../ExistedOrder';
import type { CreateAutoCreditProps } from '../../types/form-types';
import type { StockBoundElementType } from '../../types/products';
import { StockBoundSkeleton } from '../../ui';

interface IProps {
  stockBounds: StockBoundElementType[];
  setInfoModalData: (data: StockBoundElementType) => void;
  isLoading: boolean;
  handleStockBoundChange: (uuid: string) => void;
}

const StockBounds: React.FC<IProps> = ({
  stockBounds,
  setInfoModalData,
  isLoading,
  handleStockBoundChange
}) => {
  const { isFieldsFrozen } = useContext(CreateTabContext);
  const { control, trigger } = useFormContext<CreateAutoCreditProps>();
  const [allStockBounds, costCar, initialPayment, loanTerm, proofInCome] =
    useWatch({
      control,
      name: [
        'stockBounds',
        'costCar',
        'initialPayment',
        'loanTerm',
        'proofInCome'
      ]
    });

  const numberedCostCar = Number(costCar.replace(/\D/g, ''));
  const numberedInitialPayment = Math.floor(
    // В процентах
    (Number(initialPayment.replace(/\D/g, '')) / numberedCostCar) * 100
  );
  const numberedLoanTerm = Number(loanTerm);

  useEffect(() => {
    trigger('stockBounds');
  }, [costCar, initialPayment, loanTerm, proofInCome]);

  return (
    <div className="stock-bounds">
      <div className="stock-bounds__list">
        {isLoading ? (
          <StockBoundSkeleton />
        ) : (
          stockBounds.map((item) => {
            const index = allStockBounds.findIndex(
              (bound) => bound.uuid === item.uuid
            );

            return (
              <Controller
                key={item.uuid}
                control={control}
                name={`stockBounds.${index}`}
                rules={{
                  validate: () => {
                    if (!item.checked) return;

                    const conditions = item.productConditions.some(
                      (item) => item.isProofIncome === proofInCome
                    )
                      ? item.productConditions.filter(
                          (item) => item.isProofIncome === proofInCome
                        )
                      : item.productConditions;

                    const errors: string[] = [];
                    let isHasAcceptedCondition = false;

                    conditions.forEach((condition, index) => {
                      const savedLength = errors.length;
                      const startText =
                        conditions.length > 1
                          ? `<b>Условие ${index + 1}: </b>`
                          : '';

                      if (
                        numberedCostCar <
                          (Number(condition.loanAmountFrom) || -Infinity) ||
                        numberedCostCar >
                          (Number(condition.loanAmountTo) || Infinity)
                      ) {
                        const fromText = condition.loanAmountFrom
                          ? `от ${Number(condition.loanAmountFrom).toLocaleString('ru-RU')} `
                          : '';
                        const toText = condition.loanAmountTo
                          ? `до ${Number(condition.loanAmountTo).toLocaleString('ru-RU')}`
                          : '';
                        errors.push(
                          `${startText}Стоимость авто должна быть ${fromText}${toText}`
                        );
                      }
                      if (
                        numberedInitialPayment < condition.initialPaymentFrom ||
                        numberedInitialPayment > condition.initialPaymentTo
                      ) {
                        const fromText = condition.initialPaymentFrom
                          ? `от ${condition.initialPaymentFrom}% `
                          : '';
                        const toText = condition.initialPaymentTo
                          ? `до ${condition.initialPaymentTo}%`
                          : '';
                        errors.push(
                          `${startText}Начальный взнос должен быть ${fromText}${toText} от стоимости авто`
                        );
                      }
                      if (
                        numberedLoanTerm <
                          (condition.loanPeriodFrom || -Infinity) ||
                        numberedLoanTerm > (condition.loanPeriodTo || Infinity)
                      ) {
                        const fromText = condition.loanPeriodFrom
                          ? `от ${condition.loanPeriodFrom} `
                          : '';
                        const toText = condition.loanPeriodTo
                          ? `до ${condition.loanPeriodTo}`
                          : '';
                        errors.push(
                          `${startText}Срок кредита должен быть ${fromText}${toText}`
                        );
                      }
                      if (condition.isProofIncome && !proofInCome) {
                        errors.push(
                          `${startText}Необходим документ, подтверждающий доход`
                        );
                      } else if (!condition.isProofIncome && proofInCome) {
                        errors.push(
                          `${startText}Не нужен документ, подтверждающий доход`
                        );
                      }

                      if (errors.length === savedLength)
                        isHasAcceptedCondition = true;
                    });

                    if (errors.length > 0 && !isHasAcceptedCondition) {
                      return errors
                        .map((item) => `<p>&bull; ${item}</p>`)
                        .join('');
                    }
                  }
                }}
                render={({ fieldState: { error } }) => (
                  <div
                    className={`stock-bounds__switch-block ${error ? 'stock-bounds__switch-block--error' : ''}`}
                    key={item.uuid}
                  >
                    <Tooltip
                      open={error?.message === undefined ? false : undefined}
                      title={
                        <div
                          className="stock-bounds__info-tooltip"
                          dangerouslySetInnerHTML={{
                            __html: error?.message || ''
                          }}
                        ></div>
                      }
                    >
                      <label className="stock-bounds__switch-checkbox">
                        <Switch
                          disabled={isFieldsFrozen}
                          checked={item.checked}
                          onChange={() => handleStockBoundChange(item.uuid)}
                        />
                        <p>{item.name}</p>
                      </label>
                    </Tooltip>
                    <button
                      type="button"
                      className="stock-bounds__info-button"
                      onClick={() => setInfoModalData(item)}
                    >
                      <InfoIcon />
                    </button>
                  </div>
                )}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default StockBounds;
